'use client'

import { useEffect, useState, type CSSProperties } from 'react'
import { AmbientWorldEventKind } from '@/lib/fishing-world-catalog'
import type { FishingWorldActiveEvent } from '@/lib/fishing-world-scheduler'
import { eventNumber, eventRange, useAmbientEventCompletion } from './ambient-rendering'
import styles from './FishingScene.module.css'

export type BadgerSide = 'left' | 'right'
export type BirdPresentationMode = 'foreground-flights' | 'distant-murmuration'

type BirdChild = Readonly<{ index: number, top: number, scale: number, delayMs: number, durationMs: number, direction: 'left-to-right' | 'right-to-left', variant: number, offsetX?: number, offsetY?: number }>
export type BirdPresentation = Readonly<{ mode: BirdPresentationMode, children: readonly BirdChild[], totalDurationMs: number }>

const FOREGROUND_CHILD_LIMIT = 5
const MURMURATION_CHILD_LIMIT = 16

/** Deterministic child plans are renderer-owned: Birds still has one scheduler identity. */
export function createBirdPresentation(event: FishingWorldActiveEvent): BirdPresentation {
  const mode: BirdPresentationMode = event.kind === AmbientWorldEventKind.DistantSparrowFlock ? 'distant-murmuration' : 'foreground-flights'
  if (mode === 'distant-murmuration') {
    const count = 10 + Math.floor(eventNumber(event, 12) * (MURMURATION_CHILD_LIMIT - 9))
    const durationMs = Math.round(eventRange(event, 13, 35_000, 55_000))
    return {
      mode,
      children: Array.from({ length: count }, (_, index) => ({
        index, top: 0, scale: eventRange(event, 20 + index, 0.46, 0.78), delayMs: 0, durationMs,
        direction: eventNumber(event, 14) > 0.5 ? 'left-to-right' : 'right-to-left', variant: Math.floor(eventRange(event, 40 + index, 0, 5)),
        offsetX: eventRange(event, 60 + index, -17, 17), offsetY: eventRange(event, 80 + index, -10, 10),
      })),
      totalDurationMs: durationMs,
    }
  }

  const count = 3 + Math.floor(eventNumber(event, 12) * (FOREGROUND_CHILD_LIMIT - 2))
  const leadDirection = eventNumber(event, 13) > 0.5 ? 'left-to-right' : 'right-to-left'
  const children = Array.from({ length: count }, (_, index) => {
    const oppositeEdge = index > 1 && eventNumber(event, 20 + index) > 0.76
    const direction: BirdChild['direction'] = oppositeEdge ? (leadDirection === 'left-to-right' ? 'right-to-left' : 'left-to-right') : leadDirection
    return {
      index,
      top: eventRange(event, 30 + index, 10, 32),
      scale: eventRange(event, 40 + index, 0.7, 1.12),
      delayMs: index * Math.round(eventRange(event, 50 + index, 260, 440)),
      durationMs: Math.round(eventRange(event, 60 + index, 15_000, 23_000)),
      direction,
      variant: Math.floor(eventRange(event, 70 + index, 0, 5)),
    }
  })
  return { mode, children, totalDurationMs: Math.max(...children.map((child) => child.delayMs + child.durationMs)) }
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!query) return
    const update = () => setReduced(query.matches)
    update()
    query.addEventListener?.('change', update)
    return () => query.removeEventListener?.('change', update)
  }, [])
  return reduced
}

function useBirdCompletion(event: FishingWorldActiveEvent | undefined, presentation: BirdPresentation | null, onComplete: AmbientWildlifeProps['onComplete'], suppressed: boolean) {
  useEffect(() => {
    if (!event || !presentation || !onComplete || suppressed) return
    const remaining = Math.max(0, event.startedAtMs + presentation.totalDurationMs - Date.now())
    const timeout = window.setTimeout(() => onComplete(event.id), remaining)
    return () => window.clearTimeout(timeout)
  }, [event?.id, event?.startedAtMs, onComplete, presentation?.totalDurationMs, suppressed])
}

interface AmbientWildlifeProps {
  birdFlightsEvent?: FishingWorldActiveEvent
  murmurationEvent?: FishingWorldActiveEvent
  badgerEvent?: FishingWorldActiveEvent
  onComplete?: (eventId: string) => void
  onBadgerVisitChange?: (side: BadgerSide | null) => void
}

export function AmbientWildlife({ birdFlightsEvent, murmurationEvent, badgerEvent, onComplete, onBadgerVisitChange }: AmbientWildlifeProps) {
  const reducedMotion = useReducedMotion()
  useAmbientEventCompletion(badgerEvent, onComplete)
  const flights = birdFlightsEvent ? createBirdPresentation(birdFlightsEvent) : null
  const murmuration = murmurationEvent ? createBirdPresentation(murmurationEvent) : null
  useBirdCompletion(birdFlightsEvent, flights, onComplete, reducedMotion)
  useBirdCompletion(murmurationEvent, murmuration, onComplete, reducedMotion)
  const badgerSide: BadgerSide | null = badgerEvent ? badgerEvent.presentation?.badgerSide ?? (eventNumber(badgerEvent, 0) > 0.5 ? 'left' : 'right') : null

  useEffect(() => {
    onBadgerVisitChange?.(badgerSide)
    return () => onBadgerVisitChange?.(null)
  }, [badgerEvent?.id, badgerSide, onBadgerVisitChange])

  if ((!birdFlightsEvent || reducedMotion) && (!murmurationEvent || reducedMotion) && !badgerEvent) return null

  return <div className={styles.ambientWildlife} aria-hidden="true">
    {birdFlightsEvent && flights && !reducedMotion && (
      <div data-testid="ambient-birds" data-birds-mode={flights.mode}>
        {flights.children.map((bird) => <div key={bird.index} className={styles.birdFlight} data-direction={bird.direction} data-testid="bird-flight" style={{ '--flight-top': `${bird.top}%`, '--flight-duration': `${bird.durationMs}ms`, '--flight-delay': `${bird.delayMs}ms`, '--flight-scale': bird.scale } as CSSProperties}>
          <i className={styles.birdSprite} data-variant={bird.variant} style={{ '--bird-index': 0 } as CSSProperties} />
        </div>)}
      </div>)}
    {murmurationEvent && murmuration && !reducedMotion && <div className={styles.murmurationFlight} data-testid="ambient-distant-sparrow-flock" data-direction={murmuration.children[0]?.direction} style={{ '--murmuration-duration': `${murmuration.totalDurationMs}ms` } as CSSProperties}>
      <div className={styles.murmurationCloud}>{murmuration.children.map((bird) => <i key={bird.index} className={styles.murmurationBird} data-testid="murmuration-bird" style={{ '--bird-offset-x': `${bird.offsetX}px`, '--bird-offset-y': `${bird.offsetY}px`, '--bird-scale': bird.scale, '--bird-phase': `${bird.index * -110}ms` } as CSSProperties} />)}</div>
    </div>}
    {badgerEvent && <div className={styles.badgerVisit} data-side={badgerSide} data-testid="ambient-badger" />}
  </div>
}
